import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header'; 
import '../assets/css/styles.min.css';
import Swal from 'sweetalert2';
import LoadingSpinner from '../components/LoadingSpinner';

const api = axios.create({
  baseURL: 'https://api.codecircle.co',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: false
});

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState({ 
    title: '',
    description: '',
    logo: null,
    images: [],
    status: 'active'
  });
  const [editActivity, setEditActivity] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImagesToDelete, setSelectedImagesToDelete] = useState([]);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/activities');
      setActivities(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Failed to fetch activities: ' + (error.response?.data?.error || error.message));
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.title || !newActivity.description || !newActivity.logo || newActivity.images.length === 0) {
      Swal.fire('Warning!', 'Please fill all fields and upload at least one image', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('title', newActivity.title);
    formData.append('description', newActivity.description);
    formData.append('logo', newActivity.logo);
    formData.append('status', newActivity.status);
    
    newActivity.images.forEach((image, index) => {
      formData.append(`images[${index}]`, image);
    });

    setLoading(true);
    try {
      await api.post('/api/activities', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      setNewActivity({ 
        title: '',
        description: '',
        logo: null,
        images: [],
        status: 'active'
      });
      setIsAddModalOpen(false);
      await fetchActivities();
      Swal.fire('Success!', 'Activity added successfully.', 'success');
    } catch (error) {
      console.error('Error adding activity:', error);
      Swal.fire('Error!', error.response?.data?.error || 'Failed to add activity', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditActivity = async () => {
    if (!editActivity.title || !editActivity.description) {
      Swal.fire('Warning!', 'Title and description are required', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('title', editActivity.title);
    formData.append('description', editActivity.description);
    formData.append('status', editActivity.status);
    formData.append('_method', 'PUT');

    if (editActivity.newLogo) {
      formData.append('logo', editActivity.newLogo);
    }

    if (editActivity.newImages) {
      editActivity.newImages.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });
    }

    if (selectedImagesToDelete.length > 0) {
      selectedImagesToDelete.forEach((id, index) => {
        formData.append(`delete_image_ids[${index}]`, id);
      });
    }

    setLoading(true);
    try {
      await api.post(`/api/activities/${editActivity.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      setEditActivity(null);
      setSelectedImagesToDelete([]);
      setIsEditModalOpen(false);
      await fetchActivities();
      Swal.fire('Success!', 'Activity updated successfully.', 'success');
    } catch (error) {
      console.error('Edit error:', error);
      Swal.fire('Error!', error.response?.data?.error || 'Failed to update activity', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActivity = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await api.delete(`/api/activities/${id}`);
        await fetchActivities();
        Swal.fire('Deleted!', 'Activity has been deleted.', 'success');
      } catch (error) {
        console.error('Delete error:', error);
        Swal.fire('Error!', error.response?.data?.error || 'Failed to delete activity', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLogoSelection = (e) => {
    if (e.target.files[0]) {
      setNewActivity({
        ...newActivity,
        logo: e.target.files[0]
      });
    }
  };

  const handleEditLogoSelection = (e) => {
    if (e.target.files[0]) {
      setEditActivity({
        ...editActivity,
        newLogo: e.target.files[0]
      });
    }
  };

  const handleImageSelection = (e) => {
    if (e.target.files) {
      setNewActivity({
        ...newActivity,
        images: [...Array.from(e.target.files)]
      });
    }
  };

  const handleEditImageSelection = (e) => {
    if (e.target.files) {
      setEditActivity({
        ...editActivity,
        newImages: [...Array.from(e.target.files)]
      });
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImagesToDelete(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      }
      return [...prev, imageId];
    });
  };

  const formatImageUrl = (image) => {
    if (!image) return '/placeholder-image.jpg';
    if (image.image_data) {
      return `data:image/jpeg;base64,${image.image_data}`;
    }
    return `https://api.codecircle.co/storage/${image.image_path}`;
  };

  // Filtering and pagination logic
  const filteredActivities = activities.filter(activity => {
    if (!searchQuery.trim()) return true;
    return activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           activity.description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentActivities = filteredActivities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  return (
    <div className="page-wrapper" id="main-wrapper" data-layout="vertical" data-navbarbg="skin6" data-sidebartype="full" data-sidebar-position="fixed" data-header-position="fixed">
      <Sidebar />
      <div className="body-wrapper">
        <Header />
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title fw-semibold mb-4">Activities Management</h5>
              
              {loading && <LoadingSpinner />}
              {error && <div className="alert alert-danger">{error}</div>}

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-3 align-items-center">
                  <div className="search-box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search activities..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select
                    className="form-select"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <button className="btn btn-primary" onClick={() => setIsAddModalOpen(true)}>
                  Add Activity
                </button>
              </div>

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Logo</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Images</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentActivities.map(activity => (
                      <tr key={activity.id}>
                        <td>{activity.id}</td>
                        <td>
                          {activity.logo_data && (
                            <img
                              src={`data:image/jpeg;base64,${activity.logo_data}`}
                              alt="Activity logo"
                              width="50"
                              height="50"
                              className="rounded-circle object-fit-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/placeholder-image.jpg';
                              }}
                            />
                          )}
                        </td>
                        <td>{activity.title}</td>
                        <td className="text-truncate" style={{maxWidth: '200px'}}>{activity.description}</td>
                        <td>
                          <div className="d-flex gap-2">
                            {activity.images.map((image) => (
                              <img
                                key={image.id}
                                src={formatImageUrl(image)}
                                alt={`Activity ${activity.id}`}
                                width="50"
                                height="50"
                                className="object-fit-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/placeholder-image.jpg';
                                }}
                              />
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${activity.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                            {activity.status}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-info me-2" onClick={() => {
                            setEditActivity({
                              ...activity,
                              newLogo: null,
                              newImages: []
                            });
                            setIsEditModalOpen(true);
                          }}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteActivity(activity.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredActivities.length)} of {filteredActivities.length} entries
                  </div>
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Activity Modal */}
        {isAddModalOpen && (
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Activity</h5>
                  <button type="button" className="btn-close" onClick={() => setIsAddModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newActivity.title}
                        onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={newActivity.description}
                        onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Logo</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleLogoSelection}
                      />
                      {newActivity.logo && (
                        <div className="mt-2">
                          <img
                            src={URL.createObjectURL(newActivity.logo)}
                            alt="Logo preview"
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            className="rounded"
                          />
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Images</label>
                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelection}
                      />
                      <small className="text-muted">You can select multiple images</small>
                      {newActivity.images.length > 0 && (
                        <div className="mt-2">
                          <p>Selected {newActivity.images.length} image(s)</p>
                          <div className="d-flex gap-2 flex-wrap">
                            {Array.from(newActivity.images).map((image, index) => (
                              <div key={index} className="position-relative">
                                <img
                                  src={URL.createObjectURL(image)}
                                  alt={`Preview #${index + 1}`}
                                  style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={newActivity.status}
                        onChange={(e) => setNewActivity({ ...newActivity, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsAddModalOpen(false)}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={handleAddActivity}>Save Activity</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Activity Modal */}
        {isEditModalOpen && editActivity && (
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Activity</h5>
                  <button type="button" className="btn-close" onClick={() => setIsEditModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editActivity.title}
                        onChange={(e) => setEditActivity({ ...editActivity, title: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={editActivity.description}
                        onChange={(e) => setEditActivity({ ...editActivity, description: e.target.value })}
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Logo</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleEditLogoSelection}
                      />
                      <div className="mt-2">
                        {editActivity.newLogo ? (
                          <img
                            src={URL.createObjectURL(editActivity.newLogo)}
                            alt="New logo preview"
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            className="rounded"
                          />
                        ) : editActivity.logo_data ? (
                          <img
                            src={`data:image/jpeg;base64,${editActivity.logo_data}`}
                            alt="Current logo"
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            className="rounded"
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Current Images</label>
                      <div className="d-flex gap-2 flex-wrap">
                        {editActivity.images.map((image) => (
                          <div key={image.id} className="position-relative">
                            <img
                              src={formatImageUrl(image)}
                              alt="Activity content"
                              style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                              className={selectedImagesToDelete.includes(image.id) ? 'opacity-50' : ''}
                            />
                            <div className="form-check position-absolute top-0 end-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedImagesToDelete.includes(image.id)}
                                onChange={() => toggleImageSelection(image.id)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedImagesToDelete.length > 0 && (
                        <small className="text-danger d-block mt-1">
                          {selectedImagesToDelete.length} image(s) marked for deletion
                        </small>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Add New Images</label>
                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept="image/*"
                        onChange={handleEditImageSelection}
                      />
                      {editActivity.newImages && editActivity.newImages.length > 0 && (
                        <div className="mt-2">
                          <p>New images selected: {editActivity.newImages.length}</p>
                          <div className="d-flex gap-2 flex-wrap">
                            {Array.from(editActivity.newImages).map((image, index) => (
                              <div key={index}>
                                <img
                                  src={URL.createObjectURL(image)}
                                  alt={`New upload #${index + 1}`}
                                  style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={editActivity.status}
                        onChange={(e) => setEditActivity({ ...editActivity, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedImagesToDelete([]);
                  }}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={handleEditActivity}>Update Activity</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;